import { authenticatedFetch } from '../lib/api';
import type { RfqExcelLookupData } from './rfq-excel.types';
import type { RfqImportMasterLookup } from './rfq-import-master-resolve';
import {
  mapPartFinderRegistryRows,
  type RfqPartFinderRecord,
} from './rfq-import-partfinder-resolve';

type Named = { _id: string; name: string };
type Port = { _id: string; name: string; country: string };

export interface CreateRfqImportLookupInput {
  categories: Named[];
  vendors: Named[];
  ports: Port[];
  vessels?: Array<{ _id: string; name: string; imoNumber?: string }>;
  /** Preloaded PartFinder rows (tests). */
  partFinderParts?: RfqPartFinderRecord[];
  /** Defaults to authenticatedFetch from admin portal. */
  fetchWithAuth?: typeof authenticatedFetch;
}

/**
 * Shared master-data lookup for Excel + PDF/IDP import.
 * Uses existing GET endpoints only (never creates master data).
 */
export function buildCreateRfqImportLookup(
  input: CreateRfqImportLookupInput
): RfqExcelLookupData & RfqImportMasterLookup {
  const fetchWithAuth = input.fetchWithAuth ?? authenticatedFetch;

  return {
    vessels: input.vessels ?? [],
    categories: input.categories,
    vendors: input.vendors,
    ports: input.ports,
    partFinderParts: input.partFinderParts,

    getSubCategoriesForCategory: async (categoryId: string) => {
      const response = await fetchWithAuth(
        `/api/v1/admin/product-hierarchy/subcategories?categoryId=${categoryId}`
      );
      if (!response.ok) return [];
      const data = await response.json();
      const list = Array.isArray(data?.data) ? data.data : [];
      return list.map((s: { _id: string; name: string }) => ({
        _id: String(s._id),
        name: s.name,
      }));
    },

    getBrandsForCategory: async (categoryId: string) => {
      const response = await fetchWithAuth(
        `/api/v1/admin/brands?status=active&categoryId=${categoryId}`
      );
      if (!response.ok) return [];
      const data = await response.json();
      const payload = data.data;
      const list = Array.isArray(payload)
        ? payload
        : payload && Array.isArray(payload.data)
          ? payload.data
          : [];
      return list.map((b: { _id: string; name: string }) => ({
        _id: String(b._id),
        name: b.name,
      }));
    },

    getModelsForBrand: async (brandId: string) => {
      const response = await fetchWithAuth(`/api/v1/admin/models?brandId=${brandId}`);
      if (!response.ok) return [];
      const data = await response.json();
      const payload = data.data;
      const list = Array.isArray(payload)
        ? payload
        : payload && Array.isArray(payload.data)
          ? payload.data
          : [];
      return list.map((m: { _id: string; name: string }) => ({
        _id: String(m._id),
        name: m.name,
      }));
    },

    getPortAgentsForPort: async (portId: string) => {
      const response = await fetchWithAuth(`/api/v1/ports/${portId}/agents`);
      if (!response.ok) return [];
      const data = await response.json();
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      return list.map((a: { _id: string; companyName: string; address?: string }) => ({
        _id: String(a._id),
        companyName: a.companyName,
        address: a.address,
      }));
    },

    getPartFinderParts: async () => {
      if (input.partFinderParts) return input.partFinderParts;
      const response = await fetchWithAuth('/api/v1/admin/product-hierarchy/partfinder');
      if (!response.ok) return [];
      const data = await response.json();
      const list = Array.isArray(data?.data) ? data.data : [];
      return mapPartFinderRegistryRows(list);
    },
  };
}
